import { Alert, Table, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

/**
 * Help center topic: standard catalog identity fields (cell category,
 * tissue origin, disease) — meanings, sources and the Cellosaurus
 * Category → catalog enum mapping.
 */
export function CellLineCatalogIdentityHelp() {
  const { i18n } = useTranslation();
  const zh = i18n.resolvedLanguage !== 'en-US';

  const fieldRows = zh
    ? [
        {
          key: 'cellCategory',
          field: '细胞类别',
          meaning: '标准库自己的分类枚举（正常/肿瘤/永生化/原代/干细胞/其他），用于筛选、展示与统计。',
          source: 'Cellosaurus Category 字段，按下方映射表归类',
          rule: '快照未给出类别时留空，不按名称推断',
        },
        {
          key: 'tissueOrigin',
          field: '组织来源',
          meaning: '细胞来源的组织或器官，例如 Lung（肺）、Bone marrow（骨髓）。',
          source: 'Cellosaurus 注释 "Derived from site" 的位点部分',
          rule: '无该注释时留空，不推断',
        },
        {
          key: 'disease',
          field: '疾病',
          meaning: '与细胞系相关的疾病，例如 Lung adenocarcinoma（肺腺癌）；多个去重后用“；”连接。',
          source: 'Cellosaurus Disease 字段',
          rule: '非肿瘤或无记录（如 Vero、WI-38）时留空',
        },
      ]
    : [
        {
          key: 'cellCategory',
          field: 'Cell category',
          meaning: 'The catalog\u2019s own enum (NORMAL/TUMOR/IMMORTALIZED/PRIMARY/STEM_CELL/OTHER) used for filtering, display and stats.',
          source: 'Cellosaurus Category, mapped by the table below',
          rule: 'Left empty when the snapshot has no category; never inferred',
        },
        {
          key: 'tissueOrigin',
          field: 'Tissue origin',
          meaning: 'Tissue or organ of origin, e.g. Lung, Bone marrow.',
          source: 'The site part of the Cellosaurus "Derived from site" comment',
          rule: 'Left empty when the comment is absent',
        },
        {
          key: 'disease',
          field: 'Disease',
          meaning: 'Disease(s) associated with the line, e.g. Lung adenocarcinoma; duplicates removed and joined with "；".',
          source: 'Cellosaurus Disease field',
          rule: 'Left empty for non-tumor lines or when no record exists (e.g. Vero, WI-38)',
        },
      ];

  const mappingRows = zh
    ? [
        ['Cancer cell line', 'TUMOR', '肿瘤'],
        ['Spontaneously immortalized cell line', 'IMMORTALIZED', '永生化'],
        ['Transformed cell line', 'IMMORTALIZED', '永生化'],
        ['Telomerase immortalized cell line', 'IMMORTALIZED', '永生化'],
        ['Conditionally immortalized cell line', 'IMMORTALIZED', '永生化'],
        ['Hybridoma', 'IMMORTALIZED', '永生化'],
        ['Finite cell line', 'NORMAL', '正常'],
        ['Induced pluripotent stem cell', 'STEM_CELL', '干细胞'],
        ['Embryonic stem cell', 'STEM_CELL', '干细胞'],
        ['其他（Somatic cell hybrid、Recombinant 等）', 'OTHER', '其他'],
      ].map(([category, code, label], index) => ({
        key: index,
        category,
        code,
        label,
      }))
    : [
        ['Cancer cell line', 'TUMOR', 'Tumor'],
        ['Spontaneously immortalized cell line', 'IMMORTALIZED', 'Immortalized'],
        ['Transformed cell line', 'IMMORTALIZED', 'Immortalized'],
        ['Telomerase immortalized cell line', 'IMMORTALIZED', 'Immortalized'],
        ['Conditionally immortalized cell line', 'IMMORTALIZED', 'Immortalized'],
        ['Hybridoma', 'IMMORTALIZED', 'Immortalized'],
        ['Finite cell line', 'NORMAL', 'Normal'],
        ['Induced pluripotent stem cell', 'STEM_CELL', 'Stem cell'],
        ['Embryonic stem cell', 'STEM_CELL', 'Stem cell'],
        ['Other (somatic cell hybrid, recombinant, etc.)', 'OTHER', 'Other'],
      ].map(([category, code, label], index) => ({
        key: index,
        category,
        code,
        label,
      }));

  return (
    <>
      <Alert
        type="info"
        showIcon
        message={
          zh
            ? '身份字段（细胞类别、组织来源、疾病）来自 Cellosaurus 56.0（2026-06-25），每条都保留原始值、来源版本与链接用于追溯；字段本身只存标准库自己的枚举或官方原文，缺失时留空，不猜测。'
            : 'Identity fields (cell category, tissue origin, disease) come from Cellosaurus 56.0 (2026-06-25); each value keeps its raw text, source version and link. Missing values are left empty, never guessed.'
        }
        style={{ marginBottom: 16 }}
      />

      <Typography.Title level={5} style={{ marginTop: 0 }}>
        {zh ? '字段说明' : 'Field reference'}
      </Typography.Title>
      <Table
        size="small"
        pagination={false}
        rowKey="key"
        dataSource={fieldRows}
        columns={[
          { title: zh ? '字段' : 'Field', dataIndex: 'field', width: 130 },
          { title: zh ? '含义' : 'Meaning', dataIndex: 'meaning' },
          { title: zh ? '数据来源' : 'Source', dataIndex: 'source', width: 260 },
          { title: zh ? '填写规则' : 'Rule', dataIndex: 'rule', width: 220 },
        ]}
        style={{ marginBottom: 16 }}
      />

      <Typography.Title level={5}>
        {zh ? '细胞类别映射表（Cellosaurus Category → 标准库枚举）' : 'Cell category mapping (Cellosaurus Category → catalog enum)'}
      </Typography.Title>
      <Table
        size="small"
        pagination={false}
        rowKey="key"
        dataSource={mappingRows}
        columns={[
          { title: 'Cellosaurus Category', dataIndex: 'category' },
          { title: zh ? '标准库枚举' : 'Catalog enum', dataIndex: 'code', width: 160 },
          { title: zh ? '中文含义' : 'Meaning', dataIndex: 'label', width: 140 },
        ]}
        style={{ marginBottom: 16 }}
      />

      <Typography.Title level={5}>
        {zh ? '来源与维护规则' : 'Provenance and maintenance rules'}
      </Typography.Title>
      <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
        <li>
          {zh
            ? '每个字段的来源记录（来源名称、版本、链接、原始值）保存在 custom_fields.identitySources，可通过标准库详情核对。'
            : 'Every field\u2019s source record (name, version, link, raw value) is kept in custom_fields.identitySources and can be checked from the catalog detail view.'}
        </li>
        <li>
          {zh
            ? '身份字段是注释性数据，不改变 AI 评估状态，也不触发重新评估。'
            : 'Identity fields are annotations; they do not change the AI evaluation status or trigger re-evaluation.'}
        </li>
        <li>
          {zh
            ? '管理员可在编辑表单中人工修正，人工修正值优先，重新播种不会覆盖已有值。'
            : 'Administrators can correct values in the edit form; manual values win and re-seeding never overwrites them.'}
        </li>
        <li>
          {zh
            ? '升级 Cellosaurus 快照时，用 scripts/build-cell-line-identity-data.ts 重新生成身份数据，并保留新快照版本作为来源版本。'
            : 'When upgrading the Cellosaurus snapshot, regenerate the identity data with scripts/build-cell-line-identity-data.ts and keep the new snapshot version as the source version.'}
        </li>
      </ul>
    </>
  );
}
